import { ReactElement } from "react";

import Box from "@mui/material/Box";
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRowId,
  GridRowModes,
} from "@mui/x-data-grid-pro";
import { Edit01Icon, Save01Icon, Trash01Icon, XIcon } from "components";
import { useEditableDataGrid } from "components/DataGrid/DataGrid.hooks";
import Typography from "components/Typography";
import { colors } from "configs";
import { gray } from "configs/colors";
import ErrorCode from "configs/errorCode";
import SuccessCode from "configs/successCode";
import { message } from "contexts";
import { dayjs, dayjsFormatTZ, dayjsTZ } from "helpers";
import { ConfigFieldsExport } from "hooks/useExportExcel";
import { Status } from "services";
import { i18n } from "translations";
import { checkMainLan, rem } from "utils";
import { DataType } from "utils/xlsx";

export const renderCustomOption = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  keyLabel: string,
  keySubLabel: string
) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={rem(8)}
      overflow="hidden"
      flexWrap="wrap"
    >
      <Typography type="TextSM" fontWeight={500}>
        {data?.[keyLabel]}
      </Typography>
      <Typography type="TextXS">({data?.[keySubLabel]})</Typography>
    </Box>
  );
};

export const confirmDelete = (callback: (e?: unknown) => unknown) => {
  message({
    title: i18n.t("delete"),
    content: i18n.t("areYouSureYouWantToDelete"),
    buttonLabel: i18n.t("confirm"),
    type: "Warning",
    callback: () => {
      callback();
    },
  });
};

export const confirmChangeStatus = (callback: (e?: unknown) => unknown) => {
  message({
    title: i18n.t("confirm"),
    content: i18n.t("areYouSureYouWantToChangeStatus"),
    buttonLabel: i18n.t("confirm"),
    type: "Warning",
    callback: () => {
      callback();
    },
  });
};

export const confirmAction = (callback: (e?: unknown) => unknown) => {
  message({
    title: i18n.t("confirm"),
    content: i18n.t("areYouSure"),
    buttonLabel: i18n.t("confirm"),
    type: "Warning",
    callback: () => {
      callback();
    },
  });
};

export const notiFeatureDevelopment = () => {
  message({
    title: i18n.t("featureInDevelopment"),
    buttonLabel: i18n.t("confirm"),
    type: "Info",
  });
};

export const notiErrorCode = (
  key: keyof typeof ErrorCode,
  isWarning?: boolean
) => {
  const content = checkMainLan()
    ? ErrorCode[key]?.nativeDescription || ""
    : ErrorCode[key]?.description || "";
  message({
    title: content,
    type: isWarning ? "Warning" : "Error",
  });
};

export const notiSuccessCode = (key: keyof typeof SuccessCode) => {
  const content = checkMainLan()
    ? SuccessCode[key]?.nativeDescription || ""
    : SuccessCode[key]?.description || "";
  message({
    title: content,
    type: "Success",
  });
};

export const notiSuccess = () => {
  message({
    title: i18n.t("successfully"),
    type: "Success",
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCommonInfoTableColumns = (): GridColDef<any>[] => {
  return [
    {
      field: "createdBy",
      headerName: i18n.t("createdBy"),
      width: 200,
      renderCell: ({ row }) => (row?.createdBy?.fullName as never) ?? "",
    },
    {
      field: "createdAt",
      headerName: i18n.t("createdAt"),
      width: 200,
      renderCell: ({ row }) =>
        row?.createdAt
          ? dayjsTZ(row?.createdAt as string).format("DD/MM/YYYY HH:mm")
          : "",
    },
    {
      field: "updatedAt",
      headerName: i18n.t("updatedAt"),
      width: 200,
      renderCell: ({ row }) =>
        row?.updatedAt
          ? dayjsTZ(row?.updatedAt as string).format("DD/MM/YYYY HH:mm")
          : "",
    },
  ];
};

export const columnNonFunc = {
  resizable: false,
  sortable: false,
  disableColumnMenu: true,
};

export const columnCenter = {
  align: "center",
  headerAlign: "center",
};

export const styleTableNoBorder = {
  "& .MuiDataGrid-columnHeaders": {
    // backgroundColor: "transparent !important",
  },
  "& .MuiDataGrid-root": {
    border: "0 !important",
    boxShadow: "none !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    paddingTop: "16px !important",
    paddingBottom: "16px !important",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "18px",
    color: gray[600],
  },
  "& .MuiDataGrid-cell": {
    paddingTop: "8px !important",
    paddingBottom: "8px !important",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "18px",
    color: gray[600],
  },
};

export const styleTableWithBorder = {
  "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer":
    {
      border: "0 !important",
    },
  "& .bg-gray": {
    backgroundColor: gray[200],
  },

  "& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent":
    {
      width: "100%",
      textAlign: "center",
    },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    width: "100%",
    textAlign: "center",
    justifyContent: "center",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "grey.600",
    fontWeight: 600,
    fontSize: "14px",
    whiteSpace: "pre",
    lineHeight: "normal",
  },
  "& .MuiDataGrid-cell, .MuiDataGrid-columnHeader": {
    borderLeft: `0.5px solid ${gray[200]} !important`,
    borderRight: `0.5px solid ${gray[200]} !important`,
    borderBottom: `1px solid ${gray[200]}`,
    borderRadius: "0 !important",
  },
  "& .MuiDataGrid-columnHeader": {
    padding: "12px 20px !important",
    height: "unset !important",
    textAlign: "center",
  },
  "& .MuiDataGrid-columnHeader--filledGroup": {
    backgroundColor: gray[50],
    fontWeight: 600,
  },
};

export function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stringAvatar(name = "TLN", sx?: any) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      ...sx,
    },
    children: `${name?.split(" ")?.[0]?.[0] || ""}${
      name?.split(" ")?.[1]?.[0] || ""
    }${name?.split(" ")?.[2]?.[0] || ""}`,
  };
}

export const getDefaultExportItem = (
  name:
    | "name"
    | "code"
    | "description"
    | "effectiveFrom"
    | "status"
    | "createdBy"
    | "createdAt"
    | "updatedAt",
  order: number
): ConfigFieldsExport => {
  switch (name) {
    case "name":
      return {
        nativeName: {
          name: i18n.t("name"),
          order,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (row: any) => {
            return [row?.nativeName, row?.name]?.filter(Boolean)?.join(" / ");
          },
        },
      };

    case "code":
      return {
        code: {
          name: i18n.t("code"),
          order,
        },
      };

    case "description":
      return {
        nativeDescription: {
          name: i18n.t("description"),
          order,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (row: any) => {
            return [row?.nativeDescription, row?.description]
              ?.filter(Boolean)
              ?.join(" / ");
          },
        },
      };

    case "effectiveFrom":
      return {
        effectiveFrom: {
          name: i18n.t("effectiveFrom"),
          order,
          dataType: DataType.Date,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (row: any) => {
            return row?.effectiveFrom
              ? dayjsFormatTZ(
                  row?.effectiveFrom as string,
                  "YYYY-MM-DD"
                ).toISOString()
              : "";
          },
        },
      };

    case "status":
      return {
        status: {
          name: i18n.t("status"),
          order,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter(row: any) {
            return row.status === Status.Active
              ? i18n.t("active")
              : i18n.t("inactive");
          },
        },
      };

    case "createdBy":
      return {
        createdBy: {
          name: i18n.t("createdBy"),
          order,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter(row: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return row?.createdBy?.fullName || "";
          },
        },
      };

    case "createdAt":
      return {
        createdAt: {
          name: i18n.t("createdAt"),
          order,
          dataType: DataType.Date,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter(row: any) {
            return dayjsFormatTZ(row?.createdAt as string).toISOString();
          },
        },
      };

    case "updatedAt":
      return {
        updatedAt: {
          name: i18n.t("updatedAt"),
          order,
          dataType: DataType.Date,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter(row: any) {
            return dayjsFormatTZ(row?.updatedAt as string).toISOString();
          },
        },
      };

    default:
      return {
        test: {
          name: "test",
          order: 1,
        },
      };
  }
};

export const parsePeriod = (period?: string, format?: string) => {
  if (format) {
    if (!dayjs(period, "MM/YYYY", true).isValid())
      return {
        month: undefined,
        year: undefined,
      };
    return {
      month: dayjs(period, "MM/YYYY").month() + 1,
      year: dayjs(period, "MM/YYYY").year(),
    };
  }
  return {
    month: dayjs(period).month() + 1,
    year: dayjs(period).year(),
    monthFormat: dayjs(period).format("MM"),
  };
};

export const periodConfig = {
  openTo: "month",
  views: ["year", "month"],
  format: "MM/YYYY",
} as never;

export const periodConfigWithClear = {
  openTo: "month",
  views: ["year", "month"],
  format: "MM/YYYY",
  showClear: true,
} as never;

export const sxValidateTable = {
  "& .invalid-cell": {
    backgroundColor: colors.error[300],
  },
  "& .validate-row:has(.invalid-cell)": {
    backgroundColor: colors.warning[50],
  },
  "& .validate-row": {
    backgroundColor: colors.success[50],
  },
  "& .highlight": {
    backgroundColor: colors.warning[50],
  },
};

export const getActionsTable = (
  id: GridRowId,
  configs: {
    rowModesModel: ReturnType<
      typeof useEditableDataGrid
    >["actionProps"]["rowModesModel"];
    handleSaveClick: ReturnType<
      typeof useEditableDataGrid
    >["actionProps"]["handleSaveClick"];
    handleCancelClick: ReturnType<
      typeof useEditableDataGrid
    >["actionProps"]["handleCancelClick"];
    handleEditClick: ReturnType<
      typeof useEditableDataGrid
    >["actionProps"]["handleEditClick"];
    handleDeleteClick: ReturnType<
      typeof useEditableDataGrid
    >["actionProps"]["handleDeleteClick"];

    hideDelete?: boolean;
    hideEdit?: boolean;
  }
): ReactElement<GridActionsCellItemProps>[] => {
  const {
    handleCancelClick,
    handleDeleteClick,
    handleEditClick,
    handleSaveClick,
    rowModesModel,
    hideDelete = false,
    hideEdit = false,
  } = configs;

  const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

  if (isInEditMode) {
    return [
      <GridActionsCellItem
        key={1}
        // eslint-disable-next-line react/jsx-no-undef
        icon={<Save01Icon fontSize="small" />}
        label="Save"
        sx={{
          color: "success.500",
        }}
        onClick={handleSaveClick(id)}
      />,
      <GridActionsCellItem
        key={2}
        icon={<XIcon fontSize="small" />}
        label="Cancel"
        className="textPrimary"
        onClick={handleCancelClick(id)}
        color="inherit"
      />,
    ];
  }

  return [
    hideEdit ? (
      <div />
    ) : (
      <GridActionsCellItem
        key={3}
        icon={<Edit01Icon fontSize="small" />}
        label="Edit"
        className="textPrimary"
        onClick={handleEditClick(id)}
        color="inherit"
      />
    ),
    hideDelete ? (
      <div />
    ) : (
      <GridActionsCellItem
        key={4}
        icon={<Trash01Icon fontSize="small" />}
        label="Delete"
        onClick={handleDeleteClick(id)}
        color="error"
      />
    ),
  ];
};

export const getRangeDateByPeriod = (params: {
  from?: number; // 1 - > 31
  to?: number; // 1 - > 31
  period?: string;
}) => {
  try {
    const formatDate = (number?: number) => {
      if (!number) return "";
      return number < 10 ? `0${number}` : number;
    };
    let { from, to } = params;
    const { period } = params;
    if (!from && !to) {
      from = dayjs(period).startOf("month").date();
      to = dayjs(period).endOf("month").date();
    }
    const periodFormat = dayjs(period).format("YYYY-MM");
    // check if day invalid fill last day of month
    const fromISO = dayjs(
      `${periodFormat}-${formatDate(from)}`,
      "YYYY-MM-DD",
      true
    )?.isValid()
      ? dayjsFormatTZ(
          `${periodFormat}-${formatDate(from)}`,
          "YYYY-MM-DD"
        )?.toISOString()
      : dayjs(period).startOf("month").toISOString();
    const toISO = dayjs(
      `${periodFormat}-${formatDate(to)}`,
      "YYYY-MM-DD",
      true
    )?.isValid()
      ? dayjsFormatTZ(
          `${periodFormat}-${formatDate(to)}`,
          "YYYY-MM-DD"
        )?.toISOString()
      : dayjs(period).endOf("month").toISOString();

    const totalDays = dayjs(toISO).diff(dayjs(fromISO), "day") + 1;

    return {
      to: toISO,
      from: fromISO,
      totalDays,
    };
  } catch {
    return {
      to: "",
      from: "",
      totalDays: 0,
    };
  }
};

export const convertTextToPascalCase = (text: string) => {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

export const parseFromToPeriod = (period: string) => {
  const from = dayjs(period).startOf("month").date();
  const to = dayjs(period).endOf("month").date();
  return {
    from,
    to,
  };
};
